import React, { useState } from "react";
import {
  Modal,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { es } from "date-fns/locale";
import { useFormik } from "formik";
import * as Yup from "yup";
import ModalUserData from "../user/modalUserData";
import { AsignarUniformes } from "../../services/uniformes.services";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import BadgeIcon from "@mui/icons-material/Badge";
import ModalCharge from "../modalCharge";
import ModalSuccess from "../modalSucces";
import ModalError from "../modalError";

const ModalAsignacionUniforme = ({ open, onClose, stock }) => {
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [entregadoPorSeleccionado, setEntregadoPorSeleccionado] =
    useState(null);

  const [openUserModal, setOpenUserModal] = useState(false);
  const [openEntregadoPorModal, setOpenEntregadoPorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [AsignacionError, setAsignacionError] = useState(false);
  const [asignacionSuccess, setAsignacionSuccess] = useState(false);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const formik = useFormik({
    initialValues: {
      fecha_entrega: "", // Cambiado a string vacío para alinearse con el formateo
      id_usuario: null,
      entregado_por: null,
    },
    validationSchema: Yup.object().shape({
      fecha_entrega: Yup.string() // Cambiado a string ya que guardaremos el formato final
        .nullable()
        .required("Debe seleccionar una fecha"),
      id_usuario: Yup.number()
        .nullable()
        .required("Debe seleccionar un usuario"),
      entregado_por: Yup.number()
        .nullable()
        .required("Debe seleccionar quién entrega"),
    }),
    onSubmit: async (values) => {
      const payload = {
        id_stock: stock?.id,
        id_usuario: values.id_usuario,
        fecha_entrega: values.fecha_entrega,
        entregado_por: values.entregado_por,
      };

      try {
        setIsLoading(true);

        await sleep(2000);
        const response = await AsignarUniformes(payload);

        setAsignacionSuccess(true);
        setTimeout(() => {
          handleClose();
          setAsignacionSuccess(false);
        }, 1500);
      } catch (error) {
        console.error(" ERROR al asignar uniforme:", error);
        setError(error.message || "Error inesperado al asignar uniforme");
        setAsignacionError(true);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleClose = () => {
    setUsuarioSeleccionado(null);
    setEntregadoPorSeleccionado(null);
    formik.resetForm();
    onClose();
  };

  const handleSelectUsuario = (user) => {
    setUsuarioSeleccionado(user);
    formik.setFieldValue("id_usuario", user.id);
    formik.setFieldTouched("id_usuario", true);
    setOpenUserModal(false);
  };

  const handleSelectEntregadoPor = (user) => {
    setEntregadoPorSeleccionado(user);
    formik.setFieldValue("entregado_por", user.id);
    formik.setFieldTouched("entregado_por", true);
    setOpenEntregadoPorModal(false);
  };

  const accentYellow = "rgb(206,220,0)";
  const accentBlue = "rgb(62,67,150)";
  const bgDark = "#1a2035";
  const bgCard = "#242b43";
  const borderBlue = "1.5px solid rgb(62,67,150)";

  const selectorButtonSx = (selected, hasError) => ({
    justifyContent: "flex-start",
    backgroundColor: bgCard,
    border: `1px solid ${hasError ? "#e24b4a" : selected ? accentYellow : accentBlue}`,
    color: selected ? "white" : "#8892b0",
    borderRadius: "8px",
    py: 1.2,
    textTransform: "none",
    fontSize: 14,
    "&:hover": {
      borderColor: accentYellow,
      backgroundColor: bgCard,
    },
  });

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            backgroundColor: bgDark,
            borderRadius: "14px",
            border: borderBlue,
            width: 420,
            mx: "auto",
            mt: "8%",
            overflow: "hidden",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              backgroundColor: bgCard,
              borderBottom: borderBlue,
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: "rgba(62,67,150,0.25)",
                border: "1px solid rgb(62,67,150)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckroomIcon sx={{ color: accentYellow, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: 20, fontWeight: 500, color: "white" }}
              >
                Asignar uniforme
              </Typography>
              <Typography sx={{ fontSize: 16, color: "#8892b0" }}>
                Completá los datos de entrega
              </Typography>
            </Box>
          </Box>

          {/* BODY */}
          <Box
            sx={{
              px: 3,
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* USUARIO */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#8892b0",
                  textTransform: "uppercase",
                  mb: 0.75,
                }}
              >
                Usuario (recibe)
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonSearchIcon />}
                onClick={() => setOpenUserModal(true)}
                sx={selectorButtonSx(
                  usuarioSeleccionado,
                  formik.touched.id_usuario &&
                    Boolean(formik.errors.id_usuario),
                )}
              >
                {usuarioSeleccionado
                  ? `${usuarioSeleccionado.firstname} ${usuarioSeleccionado.lastname}`
                  : "Seleccionar usuario"}
              </Button>
              {formik.touched.id_usuario && formik.errors.id_usuario && (
                <Typography
                  sx={{ fontSize: 12, color: "#e24b4a", mt: 0.5, ml: 0.25 }}
                >
                  {formik.errors.id_usuario}
                </Typography>
              )}
            </Box>

            {/* ENTREGADO POR */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#8892b0",
                  textTransform: "uppercase",
                  mb: 0.75,
                }}
              >
                Entregado por
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BadgeIcon />}
                onClick={() => setOpenEntregadoPorModal(true)}
                sx={selectorButtonSx(
                  entregadoPorSeleccionado,
                  formik.touched.entregado_por &&
                    Boolean(formik.errors.entregado_por),
                )}
              >
                {entregadoPorSeleccionado
                  ? `${entregadoPorSeleccionado.firstname} ${entregadoPorSeleccionado.lastname}`
                  : "Seleccionar quien entrega"}
              </Button>
              {formik.touched.entregado_por && formik.errors.entregado_por && (
                <Typography
                  sx={{ fontSize: 12, color: "#e24b4a", mt: 0.5, ml: 0.25 }}
                >
                  {formik.errors.entregado_por}
                </Typography>
              )}
            </Box>

            {/* FECHA */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#8892b0",
                  textTransform: "uppercase",
                  mb: 0.75,
                }}
              >
                Fecha de entrega
              </Typography>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
              >
                <DatePicker
                  value={
                    formik.values.fecha_entrega
                      ? dayjs(formik.values.fecha_entrega)
                      : null
                  }
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      "fecha_entrega",
                      newValue ? newValue.format("YYYY-MM-DD HH:mm:ss") : "",
                    );
                    formik.setFieldTouched("fecha_entrega", true);
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.fecha_entrega &&
                        Boolean(formik.errors.fecha_entrega),
                      helperText:
                        formik.touched.fecha_entrega &&
                        formik.errors.fecha_entrega,
                      onBlur: () =>
                        formik.setFieldTouched("fecha_entrega", true),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: bgCard,
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor:
                          formik.touched.fecha_entrega &&
                          formik.errors.fecha_entrega
                            ? "#e24b4a"
                            : formik.values.fecha_entrega
                              ? accentYellow
                              : accentBlue,
                      },
                      "&:hover fieldset": { borderColor: accentYellow },
                      "&.Mui-focused fieldset": { borderColor: accentYellow },
                    },
                    "& .MuiSvgIcon-root": { color: "#8892b0" },
                    "& .MuiFormHelperText-root": { color: "#e24b4a" },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {/* BOTONES */}
          <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading}
              onClick={() => {
                formik.setTouched({
                  fecha_entrega: true,
                  id_usuario: true,
                  entregado_por: true,
                });
                formik.handleSubmit();
              }}
              sx={{
                backgroundColor: accentYellow,
                color: "#1a2035",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                py: 1.25,
                "&:hover": { backgroundColor: "#b8c400" },
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Confirmar"
              )}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              disabled={isLoading}
              sx={{
                borderColor: accentBlue,
                color: "#8892b0",
                borderRadius: "8px",
                textTransform: "none",
                py: 1.25,
                "&:hover": {
                  backgroundColor: "rgba(62,67,150,0.1)",
                  borderColor: accentBlue,
                },
              }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* MODAL USUARIO (Sin filtros de grupo) */}
      <ModalUserData
        open={openUserModal}
        onClose={() => setOpenUserModal(false)}
        onSelectUsuario={handleSelectUsuario}
      />

      {/* MODAL RRHH (Filtrado por id_grupo = 2) */}
      <ModalUserData
        open={openEntregadoPorModal}
        onClose={() => setOpenEntregadoPorModal(false)}
        onSelectUsuario={handleSelectEntregadoPor}
        idGrupoFiltro={2}
      />

      <ModalCharge open={isLoading} />
      <ModalSuccess
        open={asignacionSuccess}
        onClose={() => setAsignacionSuccess(false)}
        message="Uniforme asignado correctamente"
      />
      <ModalError
        open={AsignacionError}
        onClose={() => setAsignacionError(false)}
        message={error}
      />
    </>
  );
};

export default ModalAsignacionUniforme;
